import { z } from "zod";
import {
  UuidField,
  ParticipantIdField,
  TimestampField,
  MetadataField,
  ErrorResponse,
  WatcherOperation,
  createWatcherData,
  TimestampsFields,
} from "./common";

const ParticipantStatus = z.enum(["online", "offline", "away", "busy", "typing"]);
const ParticipantType = z.enum(["user", "bot"]);
const ParticipantRole = z.enum(["admin", "member", "guest", "moderator", "viewer"]);

const Participant = z.object({
  id: UuidField,
  name: z.string().min(1).max(100),
  type: ParticipantType,
  avatar: z.string().max(500).optional(),
  status: ParticipantStatus,
  role: ParticipantRole.nullable(),
  groupId: UuidField.nullable(),
  lastSeen: TimestampField.nullable(),
  metadata: MetadataField,
  workspaceId: UuidField,
  participantId: ParticipantIdField, // Client-generated device fingerprint hash
  registeredUserId: UuidField.nullable(),
  ...TimestampsFields,
});
const ParticipantListResponse = z.object({
  participants: z.array(Participant),
  total: z.number().int().gte(0),
});
const CreateParticipantRequest = z.object({
  name: z.string().min(1).max(100),
  type: ParticipantType,
  avatar: z.string().max(500).optional(),
  status: ParticipantStatus.optional().default("offline"),
  role: ParticipantRole.optional(),
  groupId: UuidField.optional(),
  workspaceId: UuidField,
  participantId: ParticipantIdField,
  registeredUserId: UuidField.optional(),
  metadata: MetadataField,
});
const UpdateParticipantRequest = z
  .object({
    status: ParticipantStatus,
    role: ParticipantRole,
    groupId: UuidField,
    metadata: z.object({}).partial(),
  })
  .partial();
const ParticipantGroup = z.object({
  id: UuidField,
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  isExpanded: z.boolean().optional().default(true),
  workspaceId: UuidField,
  ...TimestampsFields,
});
const ParticipantGroupListResponse = z.object({
  groups: z.array(ParticipantGroup),
  total: z.number().int().gte(0),
});
const CreateParticipantGroupRequest = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  workspaceId: UuidField,
});
const UpdateParticipantGroupRequest = z
  .object({
    name: z.string().min(1).max(100),
    description: z.string().max(500),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    isExpanded: z.boolean(),
  })
  .partial();
const GroupWithParticipants = z.object({
  group: z.union([ParticipantGroup, z.object({}).partial()]).optional(),
  participants: z.array(Participant),
});
const PartyStats = z.object({
  totalParticipants: z.number().int().gte(0),
  onlineParticipants: z.number().int().gte(0),
  totalGroups: z.number().int().gte(0),
  participantsByStatus: z
    .object({
      online: z.number().int().gte(0),
      offline: z.number().int().gte(0),
      away: z.number().int().gte(0),
      busy: z.number().int().gte(0),
      typing: z.number().int().gte(0),
    })
    .partial()

    .optional(),
  participantsByType: z
    .object({ user: z.number().int().gte(0), bot: z.number().int().gte(0) })
    .partial()

    .optional(),
});
const WorkspacePartyResponse = z.object({
  workspaceId: UuidField,
  participants: z.array(Participant),
  groups: z.array(ParticipantGroup),
  participantsByGroup: z.array(GroupWithParticipants),
  stats: PartyStats,
});

// Watcher callback data structures
const ParticipantWatchData = createWatcherData(Participant);
const GroupWatchData = createWatcherData(ParticipantGroup);

// Party Event types for real-time updates
const ParticipantStatusEvent = z.object({
  participantId: z.string(),
  oldStatus: ParticipantStatus,
  newStatus: ParticipantStatus,
  participant: Participant.optional(),
  threadId: z.string().optional(),
  conversationId: z.string().optional(),
});

const ParticipantJoinEvent = z.object({
  participant: Participant,
});

const ParticipantLeaveEvent = z.object({
  participantId: z.string(),
  participantName: z.string(),
});

const GroupUpdateEvent = z.object({
  groupId: z.string(),
  changes: z.record(z.string(), z.unknown()),
  group: ParticipantGroup.optional(),
});

const PartyEvent = z.object({
  id: z.string(),
  type: z.enum([
    "participant-status",
    "participant-join",
    "participant-leave",
    "group-update",
  ]),
  workspaceId: UuidField,
  timestamp: z.string(),
  data: z.union([
    ParticipantStatusEvent,
    ParticipantJoinEvent,
    ParticipantLeaveEvent,
    GroupUpdateEvent,
  ]),
});

export const schemas = {
  Participant,
  ParticipantListResponse,
  ErrorResponse,
  CreateParticipantRequest,
  UpdateParticipantRequest,
  ParticipantGroup,
  ParticipantGroupListResponse,
  CreateParticipantGroupRequest,
  UpdateParticipantGroupRequest,
  GroupWithParticipants,
  PartyStats,
  WorkspacePartyResponse,
  WatcherOperation,
  ParticipantWatchData,
  GroupWatchData,
  ParticipantStatusEvent,
  ParticipantJoinEvent,
  ParticipantLeaveEvent,
  GroupUpdateEvent,
  PartyEvent,
};

// oRPC Type Inference Support
// These types enable proper oRPC client generation and type inference

export type SchemaTypes = {
  [K in keyof typeof schemas]: z.infer<(typeof schemas)[K]>;
};

// Re-export for convenient access
export type * from "zod";
